import * as THREE from 'three'
import { type Observable, Subject } from 'rxjs'
import type { FeatureLine, FeatureResultMesh } from '../../mesh/feature'
import {
  ImportedModelObjectMetadataService,
  type ImportedModelSelectionTargetMetadata,
} from '../metadata'
import {
  areImportedBodyBindingIdsEquivalent,
  rebaseImportedBodyOccurrenceBindingId,
  resolveImportedBodyDefinitionBindingId,
  resolveImportedBodyOccurrenceNodeId,
} from '../utils'
import { collectAssemblyNodeIdentityLookupIds } from '../../assembly/utils/AssemblyNodePath'
import type {
  ImportedBodyEntry,
  ImportedPartBodyTopologyAsset,
  ImportedTopologySource,
} from '../loaders/types'
import type { GlbHierarchyNode } from '../loaders/glb'
import { syncImportedModelBodyRepresentations } from '../model-body/syncImportedModelBodyRepresentations'
import {
  collectImportedModelTopologyRepositories,
  ImportedModelTopologyRepository,
  isImportedModelTopologyHost as isImportedModelTopologyHostInternal,
  registerImportedModelTopologyRepository,
  resolveImportedModelTopologyRepository,
  resolveImportedModelTopologyRepositoryAtRoot,
  unregisterImportedModelTopologyRepository,
  type ImportedModelBodyTopologyOccurrenceRebinding,
} from './ImportedModelTopologyRepository'
import type { ImportedMeshTopologyBinding } from './ImportedMeshTopologyBinding'

export type { ImportedModelBodyTopologyOccurrenceRebinding } from './ImportedModelTopologyRepository'
export { collectImportedModelTopologyRepositories } from './ImportedModelTopologyRepository'

/**
 * 为目标 body 条目解析当前节点内对应的 source 条目下标。
 *
 * 设计说明：
 * 1. 单个 CAD body 可以因多材质被拆成多个 `mesh:n` 显示绑定，这些条目拥有相同 bodyId；
 * 2. occurrence 重绑定只改变作用域，不改变 mesh 级定义身份，因此优先按剥离作用域后的
 *    bodyBindingId 一对一匹配，禁止所有目标条目重复领取第一个同 bodyId source；
 * 3. 旧 hierarchy 缺少精确 binding 时，才依次按未消费 bodyId、同位置和兼容复用回退。
 *    最后的兼容复用仍会交给上层冲突检查，保证真正的一对多映射不会被静默接受。
 *
 * @param sourceBodyEntries 原始 hierarchy 节点的 body 条目。
 * @param targetBodyEntry 最终 hierarchy 中当前待匹配的 body 条目。
 * @param targetIndex 目标条目在当前节点内的顺序。
 * @param consumedSourceIndexes 当前节点已经一对一消费的 source 下标。
 * @returns source 条目下标；无法匹配时返回 -1。
 */
function resolveOccurrenceRebindingSourceBodyEntryIndex(
  sourceBodyEntries: readonly ImportedBodyEntry[],
  targetBodyEntry: ImportedBodyEntry,
  targetIndex: number,
  consumedSourceIndexes: ReadonlySet<number>,
): number {
  const sourceIndexes = sourceBodyEntries.map((_entry, index) => index)
  const targetDefinitionBindingId = resolveImportedBodyDefinitionBindingId(
    targetBodyEntry.bodyBindingId,
  )
  const definitionMatchedIndex = targetDefinitionBindingId
    ? sourceIndexes.find((sourceIndex) => (
        !consumedSourceIndexes.has(sourceIndex)
        && resolveImportedBodyDefinitionBindingId(
          sourceBodyEntries[sourceIndex]?.bodyBindingId,
        ) === targetDefinitionBindingId
      ))
    : undefined
  if (definitionMatchedIndex !== undefined) {
    return definitionMatchedIndex
  }

  const bodyIdMatchedIndex = sourceIndexes.find((sourceIndex) => (
    !consumedSourceIndexes.has(sourceIndex)
    && sourceBodyEntries[sourceIndex]?.bodyId === targetBodyEntry.bodyId
    && Boolean(sourceBodyEntries[sourceIndex]?.bodyBindingId)
  ))
  if (bodyIdMatchedIndex !== undefined) {
    return bodyIdMatchedIndex
  }

  if (
    !consumedSourceIndexes.has(targetIndex)
    && Boolean(sourceBodyEntries[targetIndex]?.bodyBindingId)
  ) {
    return targetIndex
  }

  const reusableDefinitionMatchedIndex = targetDefinitionBindingId
    ? sourceIndexes.find((sourceIndex) => (
        resolveImportedBodyDefinitionBindingId(
          sourceBodyEntries[sourceIndex]?.bodyBindingId,
        ) === targetDefinitionBindingId
      ))
    : undefined
  if (reusableDefinitionMatchedIndex !== undefined) {
    return reusableDefinitionMatchedIndex
  }

  const reusableBodyIdMatchedIndex = sourceIndexes.find((sourceIndex) => (
    sourceBodyEntries[sourceIndex]?.bodyId === targetBodyEntry.bodyId
    && Boolean(sourceBodyEntries[sourceIndex]?.bodyBindingId)
  ))
  if (reusableBodyIdMatchedIndex !== undefined) {
    return reusableBodyIdMatchedIndex
  }

  return Boolean(sourceBodyEntries[targetIndex]?.bodyBindingId) ? targetIndex : -1
}

/**
 * 创建导入模型 hierarchy 重映射前后的 body occurrence 映射。
 *
 * 原始节点与目标节点通过 Object3D uuid 表达同一个运行时对象，body 条目优先按 mesh 级定义身份
 * 一对一对齐，缺少精确 binding 时才按稳定 bodyId 与同节点条目顺序回退。该映射同时供首次项目组合与局部子树替换使用，
 * 避免两个加载阶段分别维护 bodyBindingId 规则。
 *
 * @param input 原始 hierarchy 根集合与最终 hierarchy 根集合。
 * @returns 包内 bodyBindingId 到最终零件 occurrence 的明确映射。
 */
export function createImportedModelTopologyOccurrenceRebindings(input: {
  readonly sourceRoots: readonly GlbHierarchyNode[]
  readonly targetRoots: readonly GlbHierarchyNode[]
  /** 冲突错误所属的业务上下文；未传入时沿用导入模型语义。 */
  readonly conflictContextLabel?: string
}): ImportedModelBodyTopologyOccurrenceRebinding[] {
  const sourceNodeByObjectId = new Map<string, GlbHierarchyNode>()
  /** 按 Object3D uuid 建立原始 hierarchy 节点索引。 */
  const collectSourceNode = (node: GlbHierarchyNode): void => {
    sourceNodeByObjectId.set(node.objectId, node)
    node.children.forEach(collectSourceNode)
  }
  input.sourceRoots.forEach(collectSourceNode)

  const rebindingBySourceScopeAndBodyBindingId = new Map<
    string,
    ImportedModelBodyTopologyOccurrenceRebinding
  >()
  /** 从目标 hierarchy 逐层收集明确的 body occurrence 映射。 */
  const collectRebinding = (targetNode: GlbHierarchyNode): void => {
    const sourceNode = sourceNodeByObjectId.get(targetNode.objectId)
    const targetAssemblyNodeId = targetNode.meta?.assemblyNodeId?.trim() ?? ''
    const sourceBodyEntries = sourceNode?.meta?.bodyEntries ?? []
    const targetBodyEntries = targetNode.meta?.bodyEntries ?? []
    const consumedSourceBodyEntryIndexes = new Set<number>()

    targetBodyEntries.forEach((targetBodyEntry, targetIndex) => {
      const sourceBodyEntryIndex = resolveOccurrenceRebindingSourceBodyEntryIndex(
        sourceBodyEntries,
        targetBodyEntry,
        targetIndex,
        consumedSourceBodyEntryIndexes,
      )
      const sourceBodyEntry = sourceBodyEntries[sourceBodyEntryIndex]
      const sourceBodyBindingId = sourceBodyEntry?.bodyBindingId?.trim() ?? ''
      const targetBodyBindingId = targetBodyEntry.bodyBindingId?.trim() ?? ''
      if (!sourceBodyBindingId || !targetBodyBindingId || !targetAssemblyNodeId) {
        return
      }
      consumedSourceBodyEntryIndexes.add(sourceBodyEntryIndex)

      /**
       * 同一 complete GLB 中多个 occurrence 可以共享定义级 bodyBindingId，但它们一定拥有独立的
       * 场景选择对象。将来源对象纳入映射主键后，既保留同源几何复用，也不会把两个组件误判为冲突。
       */
      const sourceObjectId = sourceNode?.meta?.selectionObjectId?.trim()
        || sourceNode?.objectId.trim()
        || targetNode.objectId.trim()
      const sourceOccurrenceNodeId = resolveImportedBodyOccurrenceNodeId(sourceBodyBindingId)
      const rebindingKey = sourceOccurrenceNodeId
        ? `occurrence\u0000${sourceBodyBindingId}`
        : `object\u0000${sourceObjectId}\u0000${sourceBodyBindingId}`

      const nextRebinding: ImportedModelBodyTopologyOccurrenceRebinding = {
        ...(sourceOccurrenceNodeId ? {} : { sourceObjectId }),
        sourceBodyBindingId,
        targetBodyBindingId,
        targetAssemblyNodeId,
      }
      const existingRebinding = rebindingBySourceScopeAndBodyBindingId.get(rebindingKey)
      if (
        existingRebinding
        && (
          existingRebinding.targetBodyBindingId !== targetBodyBindingId
          || existingRebinding.targetAssemblyNodeId !== targetAssemblyNodeId
        )
      ) {
        const conflictContextLabel = input.conflictContextLabel?.trim() || '导入模型'
        throw new Error(
          `${conflictContextLabel} body occurrence 映射冲突：${sourceBodyBindingId}`,
        )
      }
      rebindingBySourceScopeAndBodyBindingId.set(rebindingKey, nextRebinding)
    })
    targetNode.children.forEach(collectRebinding)
  }
  input.targetRoots.forEach(collectRebinding)
  return [...rebindingBySourceScopeAndBodyBindingId.values()]
}

/**
 * 导入模型 topology owner 被移除事件。
 *
 * 说明：
 * 1. 事件来源于 imported topology runtime 的资源释放入口；
 * 2. `root` 用于上层 Viewer 判断该释放是否属于当前场景，避免缓存模板释放误清理草图引用；
 * 3. 对外只暴露稳定 body binding 身份，不暴露即将销毁的 THREE 对象引用。
 */
export interface ImportedModelTopologyOwnerRemovedEvent {
  type: 'topology-owner-removed'
  root: THREE.Object3D
  ownerKind: 'imported-model-body'
  ownerId: string
  bodyBindingId: string
}

/**
 * imported topology owner 释放事件流。
 */
const importedModelTopologyOwnerRemovedSubject = new Subject<ImportedModelTopologyOwnerRemovedEvent>()

/**
 * 监听 imported topology owner 释放事件。
 *
 * @returns imported topology owner 释放事件流。
 */
export function observeImportedModelTopologyOwnerRemoved(): Observable<ImportedModelTopologyOwnerRemovedEvent> {
  return importedModelTopologyOwnerRemovedSubject.asObservable()
}

/**
 * 导入可见渲染 mesh 标记。
 *
 * 设计说明：
 * 1. 新架构下可见层继续使用原始 GLB mesh，因此通常不会再创建 render proxy；
 * 2. 保留该标记函数是为了兼容旧调用方与旧测试，不让上层导入高亮链出现编译断裂；
 * 3. 后续若彻底删除旧 overlay 假设，可再统一移除此兼容出口。
 */
const IMPORTED_RENDER_PROXY_FLAG = 'isImportedModelRenderMeshProxy'

/**
 * 判断对象是否为导入可见渲染 mesh。
 *
 * @param object 目标对象。
 * @returns 是否为导入可见渲染 mesh。
 */
export function isImportedModelRenderProxyMesh(object: THREE.Object3D | null | undefined): object is THREE.Mesh {
  return Boolean(object instanceof THREE.Mesh && object.userData?.[IMPORTED_RENDER_PROXY_FLAG] === true)
}

/**
 * 判断对象是否为导入拓扑 pick host。
 *
 * @param object 目标对象。
 * @returns 是否为导入拓扑 pick host。
 */
export function isImportedModelTopologyHost(
  object: THREE.Object3D | null | undefined,
): boolean {
  return isImportedModelTopologyHostInternal(object)
}

/**
 * 读取全部 imported Body 拓扑仓库的候选语义修订。
 *
 * @returns 任一仓库 Body 成员变化后都会推进的单调值。
 */
export function getImportedModelTopologySemanticRevision(): number {
  return ImportedModelTopologyRepository.getSemanticRevision()
}

/**
 * 将空闲 topology pick host 从活动实例投影安全切换到缓存态。
 *
 * @param host 即将脱离场景但继续保留几何/BVH 的宿主。
 * @returns 找到权威 Repository 并完成投影解除时返回 true。
 */
export function suspendImportedModelTopologyHost(host: FeatureResultMesh): boolean {
  return resolveImportedModelTopologyRepository(host)?.suspendPickHost(host) ?? false
}

/**
 * 将 body 级拓扑资产写回零件资产。
 *
 * 设计说明：
 * 1. hydration 阶段只负责“下载并恢复权威 BRep 拓扑资产”，不触碰场景树；
 * 2. 真正的 three.js 运行时对象由实例激活阶段按需创建，避免污染缓存模板；
 * 3. 这样同一份 PartAsset 可被多个实例复用，而不共享交互状态。
 *
 * @param asset 零件资产。
 * @param bodyTopologyAssets body 级拓扑资产表。
 */
export function applyImportedModelTopologyRuntime(
  asset: ImportedTopologySource,
  bodyTopologyAssets: NonNullable<ImportedTopologySource['bodyTopologyAssets']>,
): void {
  asset.bodyTopologyAssets = bodyTopologyAssets
}

/**
 * 在导入拓扑仓库完成激活后同步统一模型体表示。
 *
 * 模型加载存在两种合法时序：先激活拓扑再加入 Viewer，或先加入 Viewer 再异步激活拓扑。
 * Viewer.addObject 已覆盖前一种时序；这里覆盖后一种时序，确保两条时序最终都只进入同一个
 * ModelBodySceneRegistry。未挂入场景的缓存模板不会创建独立注册表，等待加入 Viewer 后再同步。
 *
 * @param root 刚完成拓扑仓库注册的导入实例根节点。
 */
function syncAttachedImportedModelBodyRepresentations(root: THREE.Object3D): void {
  let current: THREE.Object3D | null = root
  while (current) {
    if (current instanceof THREE.Scene) {
      syncImportedModelBodyRepresentations(current)
      return
    }
    current = current.parent
  }
}

/**
 * 激活导入实例的 body 级拓扑仓库。
 *
 * @param root 当前实例根节点。
 * @param asset 对应零件资产。
 */
export function activateImportedModelTopologyHosts(
  root: THREE.Object3D,
  asset?: ImportedTopologySource,
): void {
  if (!asset?.bodyTopologyAssets || !asset.bodyBrepBindings) {
    return
  }

  // 子 occurrence 激活不能释放父装配继承而来的仓库，只替换自身直接拥有的仓库。
  resolveImportedModelTopologyRepositoryAtRoot(root)?.dispose()
  const repository = new ImportedModelTopologyRepository(root)
  const bodyBindings = Object.values(asset.bodyBrepBindings)
  const sourceMeshes: THREE.Mesh[] = []

  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    if (isImportedModelTopologyHostInternal(object)) {
      return
    }

    const meshId = typeof object.userData?.partAssetLocal?.meshId === 'string'
      ? object.userData.partAssetLocal.meshId
      : ''
    if (!meshId) {
      return
    }

    if (bodyBindings.some((binding) => binding.meshId === meshId)) {
      sourceMeshes.push(object)
    }
  })

  sourceMeshes.forEach((sourceMesh) => {
    const meshId = typeof sourceMesh.userData?.partAssetLocal?.meshId === 'string'
      ? sourceMesh.userData.partAssetLocal.meshId
      : ''
    if (!meshId) {
      return
    }

    const meshBodyBindings = bodyBindings.filter((binding) => binding.meshId === meshId)
    if (meshBodyBindings.length === 0) {
      return
    }

    repository.registerVisibleMesh(sourceMesh, meshBodyBindings, asset.bodyTopologyAssets!)
  })

  if (!repository.hasRecords()) {
    return
  }

  registerImportedModelTopologyRepository(root, repository)
  syncAttachedImportedModelBodyRepresentations(root)
}

/**
 * imported topology 子树所有权迁移输入。
 */
export interface TransferImportedModelTopologySubtreeOwnershipInput {
  /** 即将从原场景位置移走的真实组件子树。 */
  readonly sourceRoot: THREE.Object3D
  /** 迁移后负责 transform、选择和释放的项目 occurrence 根。 */
  readonly targetRoot: THREE.Object3D
  /** 本次子树从包内 occurrence 到项目 occurrence 的明确映射。 */
  readonly bodyOccurrenceRebindings?: readonly ImportedModelBodyTopologyOccurrenceRebinding[]
}

/**
 * imported topology 子树所有权迁移结果。
 */
export interface TransferImportedModelTopologySubtreeOwnershipResult {
  /** 目标根是否注册了新的拓扑仓库。 */
  readonly registered: boolean
  /** 完成所有权迁移的 visible mesh 数量。 */
  readonly visibleMeshCount: number
  /** 完成所有权迁移的 body 记录数量。 */
  readonly bodyRecordCount: number
}

/**
 * 单个 visible mesh 的仓库迁移候选。
 */
interface ImportedModelTopologyMeshTransfer {
  /** 待迁移的 visible mesh。 */
  readonly mesh: THREE.Mesh
  /** 当前实际持有该 mesh body 记录的来源仓库。 */
  readonly sourceRepository: ImportedModelTopologyRepository
}

/**
 * 收集子树内真实拥有 body 记录的 visible mesh。
 *
 * @param sourceRoot 即将换父节点的组件子树。
 * @returns 按场景遍历顺序排列的迁移候选。
 */
function collectImportedModelTopologyMeshTransfers(
  sourceRoot: THREE.Object3D,
): ImportedModelTopologyMeshTransfer[] {
  const transfers: ImportedModelTopologyMeshTransfer[] = []
  const visitedMeshIds = new Set<string>()
  sourceRoot.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || isImportedModelTopologyHostInternal(object)) {
      return
    }
    if (visitedMeshIds.has(object.uuid)) {
      return
    }
    const sourceRepository = resolveImportedModelTopologyRepository(object)
    if (!sourceRepository || sourceRepository.resolveBodyRecordsByVisibleMesh(object).length === 0) {
      return
    }
    visitedMeshIds.add(object.uuid)
    transfers.push({
      mesh: object,
      sourceRepository,
    })
  })
  return transfers
}

/**
 * 校验同一批迁移候选之间不存在目标仓库无法表达的 bodyBindingId 冲突。
 *
 * 单个来源仓库到目标仓库的预检只能看到目标当前状态；在任何记录尚未写入时，多个来源
 * 仓库之间的重复 ID 仍不可见。因此整批事务必须先建立候选级唯一索引，保证后续逐 mesh
 * 写入不会在中途失败并留下半迁移状态。
 *
 * @param transfers 同一次子树所有权迁移的全部候选。
 */
function assertImportedModelTopologyMeshTransfersAreCompatible(
  transfers: readonly ImportedModelTopologyMeshTransfer[],
): void {
  const meshByBodyBindingId = new Map<string, THREE.Mesh>()
  transfers.forEach((transfer) => {
    transfer.sourceRepository.resolveBodyRecordsByVisibleMesh(transfer.mesh).forEach((record) => {
      const existingMesh = meshByBodyBindingId.get(record.bodyBindingId)
      if (existingMesh && existingMesh !== transfer.mesh) {
        throw new Error(
          `导入拓扑记录迁移冲突：同一批子树存在重复 bodyBindingId，bodyBindingId=${record.bodyBindingId}, meshes=${existingMesh.uuid},${transfer.mesh.uuid}`,
        )
      }
      meshByBodyBindingId.set(record.bodyBindingId, transfer.mesh)
    })
  })
}

/**
 * 将真实组件子树的 topology runtime 所有权转移到新的 occurrence 根。
 *
 * 设计说明：
 * 1. 仓库反查依赖 Object3D 祖先链，组件子树换父节点前必须先迁移记录；
 * 2. 所有候选先完成冲突预检，再统一写入，避免多 mesh 子树出现半迁移状态；
 * 3. occurrence 映射只应用于本次精确收集的 visible mesh records，不重新按 repository 根猜测范围；
 * 4. 空来源仓库只注销根索引，不调用 `dispose()`，因为 pick host 和 topology asset 已由目标仓库接管。
 *
 * @param input 子树和新 occurrence 根。
 * @returns 迁移统计。
 */
export function transferImportedModelTopologySubtreeOwnership(
  input: TransferImportedModelTopologySubtreeOwnershipInput,
): TransferImportedModelTopologySubtreeOwnershipResult {
  if (input.sourceRoot === input.targetRoot) {
    return {
      registered: Boolean(resolveImportedModelTopologyRepositoryAtRoot(input.targetRoot)),
      visibleMeshCount: 0,
      bodyRecordCount: 0,
    }
  }

  const transfers = collectImportedModelTopologyMeshTransfers(input.sourceRoot)
  if (transfers.length === 0) {
    return {
      registered: false,
      visibleMeshCount: 0,
      bodyRecordCount: 0,
    }
  }

  const targetRepository = resolveImportedModelTopologyRepositoryAtRoot(input.targetRoot)
    ?? new ImportedModelTopologyRepository(input.targetRoot)
  assertImportedModelTopologyMeshTransfersAreCompatible(transfers)
  transfers.forEach((transfer) => {
    transfer.sourceRepository.assertCanTransferVisibleMeshRecordsTo(
      transfer.mesh,
      targetRepository,
    )
  })

  let bodyRecordCount = 0
  const sourceRepositories = new Set<ImportedModelTopologyRepository>()
  const completedTransfers: ImportedModelTopologyMeshTransfer[] = []
  transfers.forEach((transfer) => {
    sourceRepositories.add(transfer.sourceRepository)
    const transferredRecordCount = transfer.sourceRepository.transferVisibleMeshRecordsTo(
      transfer.mesh,
      targetRepository,
    )
    bodyRecordCount += transferredRecordCount
    if (transferredRecordCount > 0) {
      completedTransfers.push(transfer)
    }
  })

  if (bodyRecordCount <= 0) {
    return {
      registered: false,
      visibleMeshCount: 0,
      bodyRecordCount: 0,
    }
  }

  try {
    const bodyOccurrenceRebindings = input.bodyOccurrenceRebindings ?? []
    if (bodyOccurrenceRebindings.length > 0) {
      targetRepository.rebindBodyOccurrences(
        bodyOccurrenceRebindings,
        new Set(completedTransfers.map((transfer) => transfer.mesh)),
      )
    }
  } catch (error) {
    /** 重绑定验证失败时恢复原仓库所有权，禁止留下“已转移但仍未提交”的半事务。 */
    completedTransfers.slice().reverse().forEach((transfer) => {
      targetRepository.transferVisibleMeshRecordsTo(
        transfer.mesh,
        transfer.sourceRepository,
      )
    })
    throw error
  }

  registerImportedModelTopologyRepository(input.targetRoot, targetRepository)
  sourceRepositories.forEach((sourceRepository) => {
    if (!sourceRepository.hasRecords()) {
      unregisterImportedModelTopologyRepository(sourceRepository.getRoot())
    }
  })
  return {
    registered: true,
    visibleMeshCount: transfers.length,
    bodyRecordCount,
  }
}

//------ 装配体引用复制拓扑选择修复 开始------
/**
 * 复制导入模型 topology runtime 的输入。
 */
export interface CloneImportedModelTopologyRuntimeInput {
  /** 来源实例根对象。 */
  readonly sourceRoot: THREE.Object3D
  /** 复制体实例根对象。 */
  readonly clonedRoot: THREE.Object3D
  /**
   * 来源拓扑资产兜底。
   *
   * 设计说明：
   * 复制体优先镜像来源实例现有的 topology runtime；当来源实例还没有仓库记录，
   * 或复制来源是树节点导致运行时记录不可遍历时，用这份资产直接在复制体上激活。
   */
  readonly fallbackTopologySource?: ImportedTopologySource
  /**
   * 按来源对象解析对应复制体对象。
   *
   * 设计说明：
  * 克隆链路会跳过临时拓扑代理，不能用 children index 反推映射，必须由克隆阶段提供稳定映射。
  */
  readonly resolveClonedObject: (sourceObject: THREE.Object3D) => THREE.Object3D | null | undefined
  /** 实例投影激活时读取来源对象的业务逻辑子节点。 */
  readonly listSourceChildren?: (sourceObject: THREE.Object3D) => readonly THREE.Object3D[]
}

/**
 * 复制导入模型 topology runtime 的结果。
 */
export interface CloneImportedModelTopologyRuntimeResult {
  /** 是否注册了复制体拓扑仓库。 */
  readonly registered: boolean
  /** 已注册的 visible mesh 数量。 */
  readonly visibleMeshCount: number
  /** 已注册的 body topology 记录数量。 */
  readonly bodyRecordCount: number
}

/**
 * 判断对象是否可作为拓扑 visible mesh 复制候选。
 *
 * @param object 待判断对象。
 * @returns 是 Mesh 时返回 true。
 */
function isTopologyRuntimeMeshCandidate(object: THREE.Object3D | null | undefined): object is THREE.Mesh {
  return Boolean(object && (object instanceof THREE.Mesh || Reflect.get(object, 'isMesh') === true))
}

/**
 * 克隆导入模型选择目标。
 *
 * @param target 来源选择目标。
 * @returns 克隆后的选择目标；缺少 assemblyNodeId 时返回 null。
 */
function cloneImportedModelSelectionTarget(
  target: ImportedModelSelectionTargetMetadata | undefined,
): ImportedModelSelectionTargetMetadata | null {
  if (!target?.assemblyNodeId) {
    return null
  }

  return {
    assemblyNodeId: target.assemblyNodeId,
    ...(target.partUuid ? { partUuid: target.partUuid } : {}),
    ...(target.primitiveId ? { primitiveId: target.primitiveId } : {}),
    ...(target.stepFaceId ? { stepFaceId: target.stepFaceId } : {}),
  }
}

/**
 * 为复制体 visible mesh 写入 body 级选择目标。
 *
 * 设计说明：
 * 1. imported pick host 会从 visible mesh 继承 metadata；
 * 2. 复制体如果只改写了普通 selectionTarget，bodyBindingId 查找仍可能回到来源实例或缺失；
 * 3. 这里按 topology runtime 已注册的 body 记录补齐映射，保证后续 FeatureFace/Line/Point
 *    代理继承的是复制体 occurrence 的选择语义。
 *
 * @param mesh 复制体 visible mesh。
 * @param bodyRecords 该 mesh 对应的 body 记录。
 */
function rebindTopologyVisibleMeshBodySelectionTargets(
  mesh: THREE.Mesh,
  bodyRecords: readonly ImportedMeshTopologyBinding[],
): void {
  if (bodyRecords.length === 0) {
    return
  }

  const fallbackTarget = cloneImportedModelSelectionTarget(
    ImportedModelObjectMetadataService.resolveSelectionTargetFromSelfOrAncestors(mesh),
  )
  if (!fallbackTarget) {
    return
  }

  const ownMetadata = ImportedModelObjectMetadataService.peekOwnMetadata(mesh)
  const nextTargetsByBodyBindingId = {
    ...(ownMetadata?.bodyBindingSelectionTargetsById ?? {}),
  }

  bodyRecords.forEach((record) => {
    const target = cloneImportedModelSelectionTarget(
      ImportedModelObjectMetadataService.resolveBodyBindingSelectionTargetFromSelfOrAncestors(
        mesh,
        record.bodyBindingId,
      ),
    ) ?? fallbackTarget
    nextTargetsByBodyBindingId[record.bodyBindingId] = target
  })

  ImportedModelObjectMetadataService.writeMetadata(mesh, {
    bodyBindingSelectionTargetsById: nextTargetsByBodyBindingId,
  })
}

/**
 * 为指定作用域内已注册的 topology visible mesh 补齐 body 级选择目标。
 *
 * @param root topology runtime 根对象。
 * @returns 处理过的 visible mesh 数量。
 */
function rebindTopologyRuntimeVisibleMeshSelectionTargets(root: THREE.Object3D): number {
  const repository = resolveImportedModelTopologyRepository(root)
  if (!repository) {
    return 0
  }

  let reboundVisibleMeshCount = 0
  root.traverse((object) => {
    if (!isTopologyRuntimeMeshCandidate(object)) {
      return
    }

    const bodyRecords = repository.resolveBodyRecordsByVisibleMesh(object)
    if (bodyRecords.length === 0) {
      return
    }

    rebindTopologyVisibleMeshBodySelectionTargets(object, bodyRecords)
    reboundVisibleMeshCount += 1
  })

  return reboundVisibleMeshCount
}

/**
 * 统计指定作用域内已注册的 topology runtime。
 *
 * @param root topology runtime 根对象。
 * @returns visible mesh 与 body record 统计。
 */
function countTopologyRuntimeRecords(root: THREE.Object3D): {
  /** 已注册 visible mesh 数量。 */
  readonly visibleMeshCount: number
  /** 已注册 body record 数量。 */
  readonly bodyRecordCount: number
} {
  const repository = resolveImportedModelTopologyRepository(root)
  if (!repository) {
    return {
      visibleMeshCount: 0,
      bodyRecordCount: 0,
    }
  }

  let visibleMeshCount = 0
  let bodyRecordCount = 0
  root.traverse((object) => {
    if (!isTopologyRuntimeMeshCandidate(object)) {
      return
    }

    const bodyRecords = repository.resolveBodyRecordsByVisibleMesh(object)
    if (bodyRecords.length === 0) {
      return
    }

    visibleMeshCount += 1
    bodyRecordCount += bodyRecords.length
  })

  return {
    visibleMeshCount,
    bodyRecordCount,
  }
}

/**
 * 将来源实例的 imported topology runtime 注册关系复制到克隆实例。
 *
 * 设计说明：
 * 1. 点、线、面选择依赖 visible mesh -> body topology record -> pick host 的二阶段拾取链；
 * 2. Object3D 克隆只复制场景树，不会复制 WeakMap 中的拓扑仓库注册；
 * 3. 因此装配体引用复制、线性阵列等“复制实例”能力必须显式镜像这些注册关系。
 *
 * @param input 复制输入。
 * @returns 复制统计结果。
 */
export function cloneImportedModelTopologyRuntime(
  input: CloneImportedModelTopologyRuntimeInput,
): CloneImportedModelTopologyRuntimeResult {
  const clonedRepository = new ImportedModelTopologyRepository(input.clonedRoot)
  let visibleMeshCount = 0
  let bodyRecordCount = 0

  const pendingSourceObjects: THREE.Object3D[] = [input.sourceRoot]
  while (pendingSourceObjects.length > 0) {
    const sourceObject = pendingSourceObjects.pop()
    if (!sourceObject) {
      continue
    }
    const sourceChildren = input.listSourceChildren?.(sourceObject) ?? sourceObject.children
    for (let index = sourceChildren.length - 1; index >= 0; index -= 1) {
      const sourceChild = sourceChildren[index]
      if (sourceChild) {
        pendingSourceObjects.push(sourceChild)
      }
    }
    const clonedObject = input.resolveClonedObject(sourceObject)
    if (!isTopologyRuntimeMeshCandidate(sourceObject) || !isTopologyRuntimeMeshCandidate(clonedObject)) {
      continue
    }

    const sourceRepository = resolveImportedModelTopologyRepository(sourceObject)
    if (!sourceRepository) {
      continue
    }

    const bodyRecords = sourceRepository.resolveBodyRecordsByVisibleMesh(sourceObject)
    if (bodyRecords.length === 0) {
      continue
    }

    const topologyAssetsById = bodyRecords.reduce<Record<string, ImportedPartBodyTopologyAsset>>(
      (accumulator, record) => {
        accumulator[record.bodyBindingId] = record.topologyAsset
        return accumulator
      },
      {},
    )
    clonedRepository.registerVisibleMesh(
      clonedObject,
      bodyRecords.map((record) => record.bodyBinding),
      topologyAssetsById,
    )
    rebindTopologyVisibleMeshBodySelectionTargets(clonedObject, bodyRecords)
    visibleMeshCount += 1
    bodyRecordCount += bodyRecords.length
  }

  if (!clonedRepository.hasRecords()) {
    if (input.fallbackTopologySource) {
      activateImportedModelTopologyHosts(input.clonedRoot, input.fallbackTopologySource)
      const fallbackCounts = countTopologyRuntimeRecords(input.clonedRoot)
      if (fallbackCounts.bodyRecordCount > 0) {
        rebindTopologyRuntimeVisibleMeshSelectionTargets(input.clonedRoot)
        return {
          registered: true,
          visibleMeshCount: fallbackCounts.visibleMeshCount,
          bodyRecordCount: fallbackCounts.bodyRecordCount,
        }
      }
    }

    return {
      registered: false,
      visibleMeshCount,
      bodyRecordCount,
    }
  }

  registerImportedModelTopologyRepository(input.clonedRoot, clonedRepository)
  syncAttachedImportedModelBodyRepresentations(input.clonedRoot)
  rebindTopologyRuntimeVisibleMeshSelectionTargets(input.clonedRoot)
  return {
    registered: true,
    visibleMeshCount,
    bodyRecordCount,
  }
}
//------ 装配体引用复制拓扑选择修复 结束------

/**
 * 根据 visible GLB 命中结果解析 body 级拓扑记录。
 *
 * @param hit 第一阶段 visible mesh 命中结果。
 * @returns body 级拓扑记录；无法解析时返回 `null`。
 */
export function resolveImportedModelBodyTopologyRecord(
  hit: THREE.Intersection<THREE.Object3D>,
): ImportedMeshTopologyBinding | null {
  const repository = resolveImportedModelTopologyRepository(hit.object)
  if (!repository) {
    return null
  }

  return repository.resolveBodyRecordFromVisibleHit(hit)
}

/**
 * 确保当前 body 的 pick host 已创建。
 *
 * @param bodyRecord body 级拓扑记录。
 * @returns pick host。
 * @throws 当可见网格未关联导入拓扑仓库时抛出错误。
 */
export function ensureImportedModelTopologyPickHost(
  bodyRecord: ImportedMeshTopologyBinding,
): FeatureResultMesh {
  const repository = resolveImportedModelTopologyRepository(bodyRecord.visibleMesh)
  if (!repository) {
    throw new Error(`导入拓扑仓库缺失，无法创建 pick host: ${bodyRecord.bodyBindingId}`)
  }

  return repository.ensurePickHost(bodyRecord)
}

/**
 * topology runtime 中单个 body 的 Worker ownership 快照。
 *
 * 该结构只用于 scoped 子树切换准备阶段，把当前场景的真实 repository 状态交给 Worker
 * ownership 计算，不参与普通模型加载或引用解析。
 */
export interface ImportedModelTopologyRuntimeBodyOwnership {
  /** repository 当前使用的 bodyBindingId。 */
  readonly bodyBindingId: string
  /** Worker GeometryState 中的 BRep 对象 ID；缺失表示该 body 没有活动 BRep。 */
  readonly geometryObjectId: string
  /** 当前 metadata 或 occurrence 声明的组件 owner。 */
  readonly assemblyNodeId: string
  /** 当前 body 是否属于本次待替换目标子树。 */
  readonly isTarget: boolean
}

/**
 * scoped 子树 topology 清理计划。
 *
 * 计划在 Worker commit 前只保存旧记录身份，不修改 repository；`dispose()` 只能在 Worker
 * 成功接管新 BRep 后调用，从而保证失败回滚仍可继续使用旧 topology。
 */
export interface ImportedModelTopologyScopedRemovalPlan {
  /** 本次需要从 topology repository 删除的旧 bodyBindingId。 */
  readonly targetBodyBindingIds: readonly string[]
  /** 本次目标 body 对应的旧 Worker geometryObjectId。 */
  readonly targetGeometryObjectIds: readonly string[]
  /** 当前场景全部 repository body 的 ownership 快照，用于保护共享 BRep。 */
  readonly bodyOwnerships: readonly ImportedModelTopologyRuntimeBodyOwnership[]
  /**
   * 提交清理计划；重复调用保持幂等并返回 0。
   *
   * @param retainedRoot 已进入场景的新子树根；被该子树复用的记录不得释放。
   */
  readonly dispose: (retainedRoot?: THREE.Object3D) => number
}

/**
 * 判断对象是否位于任一待移除场景根内。
 *
 * @param object 候选 repository 根或 visible mesh。
 * @param roots 本次 scoped 切换会从场景摘除的旧对象根。
 * @returns 候选对象是否属于旧场景子树。
 */
function isObjectInsideAnyTopologyRemovalRoot(
  object: THREE.Object3D,
  roots: readonly THREE.Object3D[],
): boolean {
  let current: THREE.Object3D | null = object
  while (current) {
    if (roots.includes(current)) {
      return true
    }
    current = current.parent
  }
  return false
}

/**
 * 判断组件 owner 是否明确位于目标项目子树。
 *
 * 这里只比较完整规范路径及其后代，不使用同名尾段匹配，避免两个同名 occurrence 在清理时串用。
 *
 * @param assemblyNodeId body 当前 owner。
 * @param targetAssemblyNodeId scoped 目标根节点 ID。
 * @returns owner 是否属于目标根或其后代。
 */
function isTopologyAssemblyNodeInsideTargetSubtree(
  assemblyNodeId: string,
  targetAssemblyNodeId: string,
): boolean {
  const assemblyNodeLookupIds = collectAssemblyNodeIdentityLookupIds(assemblyNodeId)
  const targetLookupIds = collectAssemblyNodeIdentityLookupIds(targetAssemblyNodeId)
  return assemblyNodeLookupIds.some((assemblyNodeLookupId) => (
    targetLookupIds.some((targetLookupId) => (
      assemblyNodeLookupId === targetLookupId
      || assemblyNodeLookupId.startsWith(`${targetLookupId}/`)
    ))
  ))
}

/**
 * 读取 repository body 在当前场景中的组件 owner。
 *
 * body 级选择目标优先于普通 selection/assembly metadata；旧包 metadata 不完整时最后回退到
 * bodyBindingId 自带 occurrence，但不做名称模糊匹配。
 *
 * @param record repository body 记录。
 * @returns 当前可确认的 assemblyNodeId。
 */
function resolveTopologyBodyRecordAssemblyNodeId(
  record: ImportedMeshTopologyBinding,
): string {
  const bodySelectionTarget = ImportedModelObjectMetadataService
    .resolveBodyBindingSelectionTargetFromSelfOrAncestors(
      record.visibleMesh,
      record.bodyBindingId,
    )
  const selectionTarget = ImportedModelObjectMetadataService
    .resolveSelectionTargetFromSelfOrAncestors(record.visibleMesh)
  return bodySelectionTarget?.assemblyNodeId
    ?? selectionTarget?.assemblyNodeId
    ?? ImportedModelObjectMetadataService
      .resolveAssemblyNodeIdFromSelfOrAncestors(record.visibleMesh)
    ?? resolveImportedBodyOccurrenceNodeId(record.bodyBindingId)
}

/**
 * 收集旧场景子树内全部 Object3D 身份。
 *
 * display mesh 可能与语义零件节点同级，但它的 `selectionProxyObjectId` 仍指向语义零件；
 * 因此清理计划需要同时识别物理父链和选择代理边界。
 *
 * @param roots 本次会被移除的旧场景根。
 * @returns 旧场景子树全部 Object3D uuid。
 */
function collectTopologyRemovalObjectIds(
  roots: readonly THREE.Object3D[],
): ReadonlySet<string> {
  const objectIds = new Set<string>()
  roots.forEach((root) => {
    root.traverse((object) => {
      objectIds.add(object.uuid)
    })
  })
  return objectIds
}

/**
 * 判断 body visible mesh 是否通过 metadata 明确代理到待移除子树。
 *
 * @param record repository body 记录。
 * @param removedObjectIds 待移除子树全部 Object3D uuid。
 * @returns visible mesh 或其父链的选择代理是否落在旧目标子树。
 */
function isTopologyBodyRecordProxiedToRemovedScope(
  record: ImportedMeshTopologyBinding,
  removedObjectIds: ReadonlySet<string>,
): boolean {
  let current: THREE.Object3D | null = record.visibleMesh
  while (current) {
    const metadata = ImportedModelObjectMetadataService.readOwnMetadata(current)
    const selectionProxyObjectId = metadata?.selectionProxyObjectId?.trim() ?? ''
    const hierarchySelectionObjectId = metadata?.hierarchy?.meta?.selectionObjectId?.trim() ?? ''
    if (
      (selectionProxyObjectId && removedObjectIds.has(selectionProxyObjectId))
      || (hierarchySelectionObjectId && removedObjectIds.has(hierarchySelectionObjectId))
    ) {
      return true
    }
    current = current.parent
  }
  return false
}

/**
 * 创建局部项目子树切换的旧 topology/BRep 清理计划。
 *
 * 设计说明：
 * 1. 根 `.puppy` 可能把目标与兄弟 body 放在同一个外层 repository，不能按 repository 根整体删除；
 * 2. 调用方冻结的旧场景边界已包含 hierarchy 精确交互对象，再结合完整 bodyBindingId 与 owner 判断记录；
 * 3. `bodyOwnerships` 包含目标与兄弟记录，Worker 可据此判断同一 geometryObjectId 是否仍被范围外组件共享；
 * 4. 创建计划不释放任何资源，只有调用 `dispose()` 才会精确删除冻结的旧记录。
 *
 * @param input 当前场景根、目标身份和旧对象边界。
 * @returns 可在 Worker 成功提交后执行的一次性清理计划。
 */
export function createImportedModelTopologyScopedRemovalPlan(input: {
  /** 当前已提交项目模型的场景语义根。 */
  readonly root: THREE.Object3D
  /** 本次替换的项目子树根 assemblyNodeId。 */
  readonly targetAssemblyNodeId: string
  /** 本次场景替换会移除的旧 Object3D 根。 */
  readonly removedObjectRoots: readonly THREE.Object3D[]
  /** 当前最终 hierarchy 中属于目标子树的 bodyBindingId。 */
  readonly targetBodyBindingIds: readonly string[]
}): ImportedModelTopologyScopedRemovalPlan {
  const targetRecordsByRepository = new Map<
    ImportedModelTopologyRepository,
    Array<{
      readonly bodyBindingId: string
      readonly record: ImportedMeshTopologyBinding
    }>
  >()
  const removedObjectIds = collectTopologyRemovalObjectIds(input.removedObjectRoots)
  const bodyOwnerships: ImportedModelTopologyRuntimeBodyOwnership[] = []

  collectImportedModelTopologyRepositories(input.root).forEach((repository) => {
    const repositoryInsideRemovedScope = isObjectInsideAnyTopologyRemovalRoot(
      repository.getRoot(),
      input.removedObjectRoots,
    )
    repository.listBodyRecords().forEach((record) => {
      const assemblyNodeId = resolveTopologyBodyRecordAssemblyNodeId(record)
      const bodyBindingMatchesTarget = input.targetBodyBindingIds.some((targetBodyBindingId) => (
        areImportedBodyBindingIdsEquivalent(targetBodyBindingId, record.bodyBindingId)
      ))
      const isExplicitTarget = repositoryInsideRemovedScope
        || isObjectInsideAnyTopologyRemovalRoot(record.visibleMesh, input.removedObjectRoots)
        || isTopologyBodyRecordProxiedToRemovedScope(record, removedObjectIds)
        || bodyBindingMatchesTarget
        || isTopologyAssemblyNodeInsideTargetSubtree(
          assemblyNodeId,
          input.targetAssemblyNodeId,
        )
      const geometryObjectId = record.topologyAsset.geometryObjectId?.trim() ?? ''

      bodyOwnerships.push({
        bodyBindingId: record.bodyBindingId,
        geometryObjectId,
        assemblyNodeId: assemblyNodeId || (isExplicitTarget ? input.targetAssemblyNodeId : ''),
        isTarget: isExplicitTarget,
      })
      if (!isExplicitTarget) {
        return
      }
      const currentRecords = targetRecordsByRepository.get(repository) ?? []
      currentRecords.push({
        bodyBindingId: record.bodyBindingId,
        record,
      })
      targetRecordsByRepository.set(repository, currentRecords)
    })
  })

  const targetBodyBindingIds = Array.from(new Set(
    bodyOwnerships
      .filter((ownership) => ownership.isTarget)
      .map((ownership) => ownership.bodyBindingId),
  ))
  const targetGeometryObjectIds = Array.from(new Set(
    bodyOwnerships
      .filter((ownership) => ownership.isTarget && ownership.geometryObjectId.length > 0)
      .map((ownership) => ownership.geometryObjectId),
  ))
  let disposed = false

  return {
    targetBodyBindingIds,
    targetGeometryObjectIds,
    bodyOwnerships,
    dispose(retainedRoot?: THREE.Object3D): number {
      if (disposed) {
        return 0
      }
      let removedRecordCount = 0
      const cleanupErrors: unknown[] = []
      for (const [repository, recordSnapshots] of targetRecordsByRepository) {
        for (const snapshot of recordSnapshots) {
          try {
            const currentRecord = repository.resolveBodyRecordByBindingId(snapshot.bodyBindingId)
            if (!currentRecord || currentRecord !== snapshot.record) {
              continue
            }
            if (
              retainedRoot
              && (
                isObjectInsideAnyTopologyRemovalRoot(repository.getRoot(), [retainedRoot])
                || isObjectInsideAnyTopologyRemovalRoot(currentRecord.visibleMesh, [retainedRoot])
              )
            ) {
              continue
            }

            const removedCount = repository.disposeBodyRecords([snapshot.bodyBindingId])
            removedRecordCount += removedCount
            if (removedCount > 0) {
              importedModelTopologyOwnerRemovedSubject.next({
                type: 'topology-owner-removed',
                root: repository.getRoot(),
                ownerKind: 'imported-model-body',
                ownerId: snapshot.bodyBindingId,
                bodyBindingId: snapshot.bodyBindingId,
              })
            }
          } catch (error) {
            // 单个 topology host 释放失败不能阻断其他 repository；失败记录保留在计划中供后续重试。
            cleanupErrors.push(error)
          }
        }
      }
      if (cleanupErrors.length > 0) {
        throw new AggregateError(cleanupErrors, '局部导入模型 topology 清理失败')
      }
      disposed = true
      return removedRecordCount
    },
  }
}

/**
 * 判断 topology repository 根是否真正位于指定 scoped 子树内。
 *
 * @param repositoryRoot repository 自身作用域根。
 * @param scopedRoot 本次允许修改的场景子树根。
 * @returns repository 根属于 scopedRoot 自身或其后代时返回 true。
 */
function isTopologyRepositoryRootInsideScope(
  repositoryRoot: THREE.Object3D,
  scopedRoot: THREE.Object3D,
): boolean {
  let current: THREE.Object3D | null = repositoryRoot
  while (current) {
    if (current === scopedRoot) {
      return true
    }
    current = current.parent
  }
  return false
}

/**
 * 读取指定 scoped 子树内 topology repository 当前持有的 body binding ID。
 *
 * 设计说明：repository 是运行时 body 记录的权威来源。收集器允许从输入根向上命中祖先仓库，
 * 因此这里必须再次校验 repository 自身根位于 scoped 子树内，避免把父装配或兄弟组件的 body
 * 纳入一级零件切换。该入口只返回去重快照，不暴露 repository，也不修改资源生命周期。
 *
 * @param root 需要枚举的 scoped 场景子树根。
 * @returns scoped 子树内按场景遍历首次命中顺序去重的 body binding ID 快照。
 */
export function listScopedImportedModelTopologyBodyBindingIds(
  root: THREE.Object3D,
): string[] {
  const bodyBindingIds = new Set<string>()

  collectImportedModelTopologyRepositories(root).forEach((repository) => {
    if (!isTopologyRepositoryRootInsideScope(repository.getRoot(), root)) {
      return
    }

    repository.listBodyBindingIds().forEach((bodyBindingId) => {
      const normalizedBodyBindingId = bodyBindingId.trim()
      if (normalizedBodyBindingId) {
        bodyBindingIds.add(normalizedBodyBindingId)
      }
    })
  })

  return [...bodyBindingIds]
}

/**
 * 为单一零件 occurrence 补齐 hierarchy 未声明的运行时 Body 重绑映射。
 *
 * 零件编辑产生的 Feature Body 可能只存在于 topology repository，不一定进入
 * hierarchy bodyEntries。repository 是当前场景子树内 Body 运行时身份的权威来源；
 * 当该子树被一个项目零件接管时，所有未显式重绑的 Body 都必须归属同一最终 occurrence。
 *
 * @param input 零件运行时子树、最终 occurrence 与 hierarchy 已有映射。
 * @returns 保留原顺序并补齐 repository-only Body 的完整映射。
 */
export function completeImportedModelTopologySingleOccurrenceRebindings(input: {
  readonly root: THREE.Object3D
  readonly targetAssemblyNodeId: string
  readonly existingRebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[]
}): ImportedModelBodyTopologyOccurrenceRebinding[] {
  const completed = input.existingRebindings.map((rebinding) => ({ ...rebinding }))
  const existingSourceIds = new Set(completed.map((rebinding) => rebinding.sourceBodyBindingId))

  listScopedImportedModelTopologyBodyBindingIds(input.root).forEach((bodyBindingId) => {
    if (existingSourceIds.has(bodyBindingId)) {
      return
    }

    completed.push({
      sourceBodyBindingId: bodyBindingId,
      targetBodyBindingId: rebaseImportedBodyOccurrenceBindingId(
        bodyBindingId,
        input.targetAssemblyNodeId,
      ),
      targetAssemblyNodeId: input.targetAssemblyNodeId,
    })
    existingSourceIds.add(bodyBindingId)
  })

  return completed
}

/**
 * 将指定新场景子树内的 topology repository occurrence 按明确 hierarchy 映射重绑定。
 *
 * 设计说明：
 * 1. 该入口只处理调用方显式传入的子树，不监听场景，也不改变普通加载和查询规则；
 * 2. scoped 项目节点切换完成 hierarchy/metadata 折叠后调用它，使 Mate 引用、pick host
 *    与组件 transform root 使用同一最终 occurrence；
 * 3. repository 自己负责释放旧 pick host 与原子重建索引，调用方不接触内部记录。
 *
 * @param root 已完成最终 metadata 重绑定的新场景子树。
 * @param rebindings compose 原始 hierarchy 到最终 hierarchy 的 body occurrence 映射。
 * @returns 实际完成 occurrence 重绑定的 body 记录总数。
 */
export function rebindImportedModelTopologyRuntimeBodyOccurrences(
  root: THREE.Object3D,
  rebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[],
): number {
  let reboundBodyRecordCount = 0
  collectImportedModelTopologyRepositories(root).forEach((repository) => {
    /** 收集器允许从任意命中对象向上找到仓库；scoped 改写必须排除输入根之外的祖先仓库。 */
    if (!isTopologyRepositoryRootInsideScope(repository.getRoot(), root)) {
      return
    }
    reboundBodyRecordCount += repository.rebindBodyOccurrences(rebindings)
  })
  return reboundBodyRecordCount
}

/**
 * 按转换实体引用记录的 owner ID 与边索引回收导入模型 FeatureLine 代理。
 *
 * 设计说明：
 * 1. P1 的 topology-edge descriptor 会在预览、创建和重建阶段重新读取运行时边代理；
 * 2. 导入模型的 owner ID 可能是 `bodyBindingId`，也可能是 pick host 的 `objectId`；
 * 3. 该入口只负责回收当前运行时代理，不修改模型解析与拓扑生成逻辑。
 *
 * @param root 当前导入实例或场景根节点。
 * @param owningObjectId descriptor 记录的 owner / result object ID。
 * @param featureEdgeIndex topology edge 索引。
 * @returns 命中的 FeatureLine；未命中时返回 `null`。
 */
export function resolveImportedModelTopologyFeatureLine(
  root: THREE.Object3D | null | undefined,
  owningObjectId: string,
  featureEdgeIndex: number,
): FeatureLine | null {
  if (!root || !owningObjectId || !Number.isInteger(featureEdgeIndex)) {
    return null
  }

  for (const repository of collectImportedModelTopologyRepositories(root)) {
    const bodyRecord = repository.resolveBodyRecordByBindingRef(owningObjectId)
      ?? repository.resolveBodyRecordByRuntimeObjectId(owningObjectId)
    if (!bodyRecord) {
      continue
    }

    const pickHost = repository.ensurePickHost(bodyRecord)
    pickHost.updateMatrixWorld(true)
    return pickHost.getFeatureLineByIndex(featureEdgeIndex)
  }

  return null
}

/**
 * 按导入 body 的任意运行时身份回收 FeatureResultMesh pick host。
 *
 * 设计说明：
 * 1. 导入实体在可见层仍由 GLB mesh 承载，但 CAD 功能层只能消费 FeatureResultMesh 语义；
 * 2. 历史、草图面、基准几何和特征编辑可能保存 bodyBindingId、topology objectId、meshData id 或 geometryObjectId；
 * 3. 这里把这些身份统一收口到同一个按需创建的 pick host，避免后续功能继续为导入体分叉实现。
 *
 * @param root 当前场景、导入实例根节点或其任意子节点。
 * @param owningObjectId bodyBindingId、topology objectId、meshData id、geometryObjectId 或 pick host objectId。
 * @returns 当前导入 body 的 FeatureResultMesh pick host；无法解析时返回 `null`。
 */
export function resolveImportedModelTopologyPickHost(
  root: THREE.Object3D | null | undefined,
  owningObjectId: string | null | undefined,
): FeatureResultMesh | null {
  const normalizedOwningObjectId = owningObjectId?.trim() ?? ''
  if (!root || normalizedOwningObjectId.length === 0) {
    return null
  }

  for (const repository of collectImportedModelTopologyRepositories(root)) {
    const bodyRecord = repository.resolveBodyRecordByBindingRef(normalizedOwningObjectId)
      ?? repository.resolveBodyRecordByRuntimeObjectId(normalizedOwningObjectId)
    if (!bodyRecord) {
      continue
    }

    const pickHost = repository.ensurePickHost(bodyRecord)
    pickHost.updateMatrixWorld(true)
    return pickHost
  }

  return null
}

/**
 * 释放指定 pick host。
 *
 * @param host 目标 pick host。
 */
export function disposeImportedModelTopologyHost(host: THREE.Object3D): void {
  if (!isImportedModelTopologyHostInternal(host)) {
    return
  }

  const repository = resolveImportedModelTopologyRepository(host)
  const bodyRecord = repository?.resolveBodyRecordFromPickHost(host) ?? null
  if (!repository || !bodyRecord) {
    return
  }

  repository.disposePickHost(bodyRecord.bodyBindingId)
}

/**
 * 释放指定根节点下的全部 imported 拓扑运行时资源。
 *
 * @param root 实例根节点；引用型装配资产可能没有本地 `sceneTemplate`。
 */
export function disposeImportedModelTopologyHosts(root: THREE.Object3D | null | undefined): void {
  // 装配引用资产只复用子资产时不会持有本地场景模板，
  // 释放阶段应保持幂等而不是阻断加载事务回滚。
  if (!root) {
    return
  }

  const repositories = new Set<ImportedModelTopologyRepository>()
  root.traverse((object) => {
    /**
     * 释放边界只认当前子树内直接注册的仓库根。向上反查会把祖先仓库加入集合，
     * 删除一个尚未独立认领 topology runtime 的子节点时会连带销毁全部兄弟记录。
     */
    const repository = resolveImportedModelTopologyRepositoryAtRoot(object)
    if (repository) {
      repositories.add(repository)
    }
  })

  repositories.forEach((repository) => {
    repository.listBodyBindingIds().forEach((bodyBindingId) => {
      importedModelTopologyOwnerRemovedSubject.next({
        type: 'topology-owner-removed',
        root,
        ownerKind: 'imported-model-body',
        ownerId: bodyBindingId,
        bodyBindingId,
      })
    })
    repository.dispose()
  })
  unregisterImportedModelTopologyRepository(root)
}
